import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  IconButton,
  Collapse,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUpload,
  faChevronDown,
  faChevronRight
} from '@fortawesome/pro-regular-svg-icons'
import Button from 'src/components/era-package/forms/Button'
import FileUploadModal from 'src/components/era-package/forms/FileUploadModal'
import DisUploadForm from './DisUploadForm'
import ViewDisFileModal from './ViewDisFileModal'
import AbiEntrySummaryApiUrls from 'src/apiUrls/AbiEntrySummaryApiUrls'

const tableColumns = [
  { header: 'Agency', accessorKey: 'agency' },
  { header: 'Document Type', accessorKey: 'documentType' },
  { header: 'Status', accessorKey: 'status' },
  { header: 'Submit Time', accessorKey: 'submitTime' },
  { header: 'User ID', accessorKey: 'userId' }
]

/**
 * Creates a DIS table component bound to a specific abiTransId.
 * This factory pattern allows the component to be used as a tableComponent
 * in the formSections pattern while still having access to the entry ID.
 */
export const createDisTableSection = (abiTransId) => {
  const DisTableSection = ({ title, data = [], onDataChange }) => {
    const theme = useTheme()
        const [expandedRows, setExpandedRows] = useState({})
    const [showUploadModal, setShowUploadModal] = useState(false)
    const [uploadFormData, setUploadFormData] = useState({})
    const [showViewModal, setShowViewModal] = useState(false)
    const [selectedRow, setSelectedRow] = useState(null)
    const [snackbar, setSnackbar] = useState({ open: false, type: 'success', message: '' })

    // Fetch DIS records on mount
    useEffect(() => {
      if (!abiTransId) return
      const fetchDisData = async () => {
        try {
          const response = await fetch(
            `${AbiEntrySummaryApiUrls.GET_DIS}?abiTransId=${abiTransId}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization:
                  'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
              }
            }
          )
          const json = await response.json()
          if (Array.isArray(json.data) && onDataChange) {
            onDataChange(json.data)
          }
        } catch (error) {
          console.error('Error fetching DIS data:', error)
        }
      }
      fetchDisData()
    }, [abiTransId])

        // Toggle expand for status details row
    const handleToggleExpand = useCallback((rowIndex, row) => {
      setExpandedRows((prev) => ({
        ...prev,
        [rowIndex]: !prev[rowIndex]
      }))
      // No API call needed — statusDetails is already in row data
    }, [])

    // Handle file select from the upload modal
    const handleFileSelect = useCallback(
      async (file, formData) => {
        if (!file) return

        const apiFormData = new FormData()
        apiFormData.append('file', file)
        apiFormData.append('abiTransId', abiTransId)

        // Append the extra form fields from the upload modal
        if (formData?.agency) apiFormData.append('agency', formData.agency)
                if (formData?.documentType)
          apiFormData.append('documentType', formData.documentType)
        if (formData?.actionIdentificationNumber)
          apiFormData.append('actionIdentificationNumber', formData.actionIdentificationNumber)
        if (formData?.comment)
          apiFormData.append('comment', formData.comment)

        try {
          const response = await fetch(AbiEntrySummaryApiUrls.UPLOAD_DIS, {
            method: 'POST',
            headers: {
              Authorization:
                'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
            },
            body: apiFormData
          })

          if (!response.ok) {
            throw new Error('Error uploading DIS file')
          }

                    setSnackbar({ open: true, type: 'success', message: 'DIS file uploaded successfully' })

          // Refresh table data after upload
          const refreshResponse = await fetch(
            `${AbiEntrySummaryApiUrls.GET_DIS}?abiTransId=${abiTransId}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization:
                  'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
              }
            }
          )
          const json = await refreshResponse.json()
          if (Array.isArray(json.data) && onDataChange) {
            onDataChange(json.data)
          }
                } catch (error) {
          console.error('Error uploading DIS file:', error)
          setSnackbar({ open: true, type: 'error', message: error.message || 'Error uploading DIS file' })
        }
      },
      [onDataChange]
    )

    return (
      <Stack spacing={2}>
        {/* Upload DIS Button */}
        <Box display='flex' justifyContent='flex-end'>
          <Button
            size='medium'
            minWidth={120}
            variant='contained'
            startIcon={<FontAwesomeIcon icon={faUpload} width={14} />}
            onClick={() => setShowUploadModal(true)}
            title='Upload DIS file'
          >
            Upload DIS
          </Button>
        </Box>

        {/* DIS Table */}
        <TableContainer component={Paper} variant='outlined'>
          <Table>
            <TableHead>
              <TableRow>
                {/* Expand column */}
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    width: 50
                  }}
                />
                {tableColumns.map((col) => (
                  <TableCell
                    key={col.accessorKey}
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main
                    }}
                  >
                    {col.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={tableColumns.length + 1}
                    align='center'
                  >
                    <Typography
                      variant='body2'
                      color={theme.palette.info.main}
                    >
                      No {title} records found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, index) => (
                  <React.Fragment key={index}>
                    {/* Main Row */}
                    <TableRow
                      hover
                      onClick={() => {
                        setSelectedRow(row)
                        setShowViewModal(true)
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <IconButton
                          size='small'
                          onClick={(e) => {
                            e.stopPropagation()
                            handleToggleExpand(index, row)
                          }}
                          title='Expand agency details'
                        >
                          <FontAwesomeIcon
                            icon={
                              expandedRows[index]
                                ? faChevronDown
                                : faChevronRight
                            }
                            width={12}
                          />
                        </IconButton>
                      </TableCell>
                      {tableColumns.map((col) => (
                        <TableCell key={col.accessorKey}>
                          {row[col.accessorKey] ?? ''}
                        </TableCell>
                      ))}
                    </TableRow>

                    {/* Expanded Detail Row */}
                    <TableRow>
                      <TableCell
                        colSpan={tableColumns.length + 1}
                        sx={{
                          py: 0,
                          borderBottom: expandedRows[index]
                            ? undefined
                            : 'none'
                        }}
                      >
                                                <Collapse
                          in={expandedRows[index] || false}
                          timeout='auto'
                          unmountOnExit
                        >
                          <Box sx={{ py: 2, pl: 4 }}>
                            {row.statusDetails && row.statusDetails.length > 0 ? (
                              <Table size='small'>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Severity</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {row.statusDetails.map((detail, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell>{detail.severityCode ?? '-'}</TableCell>
                                      <TableCell>{detail.code ?? '-'}</TableCell>
                                      <TableCell>{detail.message ?? '-'}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <Typography variant='body2' color='text.secondary'>
                                No status details available.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Upload DIS File Modal */}
        <FileUploadModal
          open={showUploadModal}
          onClose={() => {
            setShowUploadModal(false)
            setUploadFormData({})
          }}
          onFileSelect={handleFileSelect}
          acceptedFileTypes={['.pdf', '.tif', '.tiff', '.jpg', '.jpeg', '.png']}
          maxFileSize={5 * 1024 * 1024}
          requiredFields={['agency', 'documentType']}
          title='Upload DIS File'
          description='Select or drag and drop your document image to upload (PDF, TIFF, JPEG, PNG)'
          formData={uploadFormData}
          setFormData={setUploadFormData}
          paperSx={{ width: '680px' }}
        >
          <DisUploadForm
            formData={uploadFormData}
            setFormData={setUploadFormData}
          />
        </FileUploadModal>
                {/* View DIS File Modal */}
        <ViewDisFileModal
          open={showViewModal}
          onClose={() => {
            setShowViewModal(false)
            setSelectedRow(null)
          }}
          rowData={selectedRow}
        />

        {/* Snackbar for upload feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.type}
            variant='filled'
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Stack>
    )
  }

  return DisTableSection
}
